/**
 * 2026-02 — Client-side retry wrapper for the TUS finalize step
 * (POST /api/tasks/.../submissions/claim-tus).
 *
 * Problem: claim-tus can return a transient 502/503/504 when Emergent
 * Object Storage hiccups during the put_object_stream phase. The backend
 * already retries 5× internally, but in rare cases the upstream is down
 * long enough that the backend exhausts and surfaces the 5xx. Pre-2026-02
 * the user saw "Finalize failed: HTTP 502 — Click Submit to retry" and
 * had to retry by hand.
 *
 * This helper transparently retries gateway-class errors (502/503/504)
 * up to MAX_ATTEMPTS times with exponential backoff (2s, 4s, 8s — total
 * worst-case ~14s of waiting before surfacing a manual retry banner).
 * 4xx errors are surfaced immediately — those won't fix themselves
 * (400 bad payload, 401/403 auth, 404 task missing, 422 schema).
 *
 * Args:
 *   url         — full claim-tus URL
 *   payload     — request body
 *   onAttempt   — optional callback({ attempt, max }) called BEFORE each
 *                 attempt past the first, so the UI can show
 *                 "Finalizing upload… (attempt 2/3)" without alarming copy
 *
 * Returns: the parsed JSON body on success.
 * Throws : Error with `.status` (HTTP code), `.body` (parsed detail), and
 *          `.attempts` (how many tries we made).
 */

const MAX_ATTEMPTS = 3;
const RETRY_BACKOFFS_MS = [2000, 4000, 8000]; // wait BEFORE attempt 2, 3, 4
const RETRYABLE_STATUSES = new Set([502, 503, 504]);

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function claimTusWithRetry(url, payload, { onAttempt } = {}) {
  let lastErr = null;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
    if (attempt > 1) {
      // Surface gentle "retrying…" status BEFORE we wait
      onAttempt && onAttempt({ attempt, max: MAX_ATTEMPTS });
      await sleep(RETRY_BACKOFFS_MS[attempt - 2]);
    }
    let resp;
    try {
      resp = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('stellar_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
    } catch (networkErr) {
      // Pure network failure (offline, DNS, abort) is also retryable —
      // most likely also a gateway / ingress hiccup on Emergent.
      lastErr = new Error(networkErr.message || 'Network error');
      lastErr.status = 0;
      lastErr.attempts = attempt;
      if (attempt < MAX_ATTEMPTS) continue;
      throw lastErr;
    }
    if (resp.ok) {
      return await resp.json();
    }
    // Parse error body once so we can include it in the thrown Error.
    let body = null;
    try { body = await resp.json(); } catch { /* swallow non-JSON */ }
    const detail =
      (body && typeof body.detail === 'object' && body.detail?.message) ||
      (body && body.detail) ||
      `HTTP ${resp.status}`;
    // Non-retryable: surface immediately.
    if (!RETRYABLE_STATUSES.has(resp.status)) {
      const err = new Error(detail);
      err.status = resp.status;
      err.body = body;
      err.attempts = attempt;
      throw err;
    }
    // Retryable — remember the latest error in case we exhaust.
    lastErr = new Error(detail);
    lastErr.status = resp.status;
    lastErr.body = body;
    lastErr.attempts = attempt;
    if (attempt >= MAX_ATTEMPTS) {
      throw lastErr;
    }
    // loop continues; the next iteration's `if (attempt > 1)` block
    // will fire the onAttempt callback + the backoff sleep.
  }
  // Defensive — should be unreachable.
  if (lastErr) throw lastErr;
  throw new Error('claim-tus retry loop exited unexpectedly');
}
